import React, { useState, useEffect, useCallback } from 'react';
import GoldSiverHeader from '../HomePage/GoldSiverHeader';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../api/axios';
import { API_ENDPOINTS } from '../../api/endpoint';
import { toast } from 'react-toastify';
import '../../assets/css/dailyGift.css';

function DailyGift() {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(true);
    const [isSpinning, setIsSpinning] = useState(false);
    const [remainingSpins, setRemainingSpins] = useState(0);
    const [maxSpins, setMaxSpins] = useState(2);
    const [prizes, setPrizes] = useState([]);
    const [prizesWonToday, setPrizesWonToday] = useState([]);
    const [lastPrize, setLastPrize] = useState(null);
    const [showPrizeModal, setShowPrizeModal] = useState(false);
    const [activeItemIndex, setActiveItemIndex] = useState(null);
    
    // Get item image - use image from API if available, otherwise generate from item name
    const getItemImage = (prize) => {
        // If image is provided from API, use it (this should be the correct path from items table)
        if (prize?.image) {
            return prize.image;
        }
        
        // Fallback: generate image path from item name
        const type = prize?.type || 'item';
        let name = prize?.name || prize?.prize_name || '';
        
        if (type === 'silver') return '/images/mock-05.png';
        if (type === 'gold') return '/images/game-ui/p2.png';
        
        // For items, remove quantity prefix (e.g., "1x Ultra Ball" -> "Ultra Ball")
        if (type === 'item' && name) {
            // Remove patterns like "1x ", "2x ", "3x ", etc. from the beginning
            name = name.replace(/^\d+x\s+/i, '');
            // Generate path based on cleaned name (replace spaces with underscores, keep original case)
            const imageFileName = name.replace(/\s+/g, '_');
            return `/images/items/${imageFileName}.png`;
        }
        
        return '/images/game-ui/itemImg.png';
    };
    
    const fetchDailySpins = useCallback(async () => {
        try {
            // Add timestamp to prevent caching
            const response = await axiosInstance.get(API_ENDPOINTS.GET_DAILY_SPINS, {
                params: {
                    _t: Date.now() // Cache buster
                }
            });
            if (response.data.success) {
                setRemainingSpins(response.data.remaining_spins);
                setMaxSpins(response.data.max_spins);
                setPrizes(response.data.available_prizes || []);
                setPrizesWonToday(response.data.prizes_won_today || []);
                console.log('Daily spins loaded:', {
                    remaining: response.data.remaining_spins,
                    max: response.data.max_spins,
                    hasRecord: response.data.has_record_today,
                    today: response.data.today_date
                });
            }
        } catch (error) {
            console.error('Error fetching daily spins:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);
    
    useEffect(() => {
        fetchDailySpins();
    }, [fetchDailySpins]);
    
    const spinWheel = async () => {
        if (isSpinning || remainingSpins <= 0) {
            if (remainingSpins <= 0) {
                toast.warning(t('No spins remaining today!'));
            }
            return;
        }
        
        setIsSpinning(true);
        setActiveItemIndex(null);
        
        // Spin animation - cycle through items
        const itemCount = displayPrizes.length;
        let currentIndex = 0;
        let speed = 80;
        let elapsed = 0;
        const totalDuration = 3000;
        
        const spinInterval = setInterval(() => {
            elapsed += speed;
            currentIndex = (currentIndex + 1) % itemCount;
            setActiveItemIndex(currentIndex);
            
            // Slow down near the end
            if (elapsed > totalDuration * 0.6) {
                speed = Math.min(speed + 20, 400);
            }
            
            if (elapsed >= totalDuration) {
                clearInterval(spinInterval);
            }
        }, speed);
        
        try {
            const response = await axiosInstance.post(API_ENDPOINTS.SPIN_WHEEL);
            
            if (response.data.success) {
                const prize = response.data.prize;
                const prizeIndex = response.data.prize_index;
                
                // Wait for animation, then show winner
                setTimeout(() => {
                    clearInterval(spinInterval);
                    setActiveItemIndex(prizeIndex);
                    
                    setTimeout(() => {
                        setLastPrize(prize);
                        setShowPrizeModal(true);
                        setRemainingSpins(response.data.remaining_spins);
                        setPrizesWonToday(prev => [...prev, {
                            ...prize,
                            won_at: new Date().toISOString()
                        }]);
                        setIsSpinning(false);
                    }, 800);
                }, totalDuration);
            } else {
                clearInterval(spinInterval);
                setActiveItemIndex(null);
                toast.error(response.data.error || t('Failed to spin'));
                setIsSpinning(false);
            }
        } catch (error) {
            clearInterval(spinInterval);
            setActiveItemIndex(null);
            console.error('Error spinning wheel:', error);
            toast.error(error.response?.data?.error || t('Failed to spin'));
            setIsSpinning(false);
        }
    };
    
    const closePrizeModal = () => {
        setShowPrizeModal(false);
        setLastPrize(null);
        setActiveItemIndex(null);
    };
    
    // Default prizes if none loaded from DB
    const displayPrizes = prizes.length > 0 ? prizes.slice(0, 9) : [
        { id: 1, name: '5x Pokeball', type: 'item', rarity: 'common' },
        { id: 2, name: '500 Silver', type: 'silver', rarity: 'common' },
        { id: 3, name: '3x Great Ball', type: 'item', rarity: 'uncommon' },
        { id: 4, name: '1000 Silver', type: 'silver', rarity: 'uncommon' },
        { id: 5, name: '1x Ultra Ball', type: 'item', rarity: 'rare' },
        { id: 6, name: '5 Gold', type: 'gold', rarity: 'rare' },
        { id: 7, name: '2x Potion', type: 'item', rarity: 'common' },
        { id: 8, name: '10 Gold', type: 'gold', rarity: 'epic' },
        { id: 9, name: '1x Master Ball', type: 'item', rarity: 'legendary' }
    ];

    if (isLoading) {
        return (
            <div>
                <GoldSiverHeader previous={'/home'} title={t('Daily Gift')}>
                    <div className="text-center py-5">
                        <div className="spinner-border text-warning" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </GoldSiverHeader>
            </div>
        );
    }

    return (
        <div>
            <GoldSiverHeader previous={'/home'} title={t('Fortune Wheel')}>
                {/* Spin Button Area */}
                <div className="container">
                    <div className="daily-gift-spin-area">
                        <div className="spin-info">
                            {/* <span className="spin-icon">🎰</span> */}
                            <span className="spin-icon"></span>
                            <span className="spin-count">
                                {t('Spins')}: <strong>{remainingSpins}</strong>/{maxSpins}
                            </span>
                        </div>
                        <button 
                            className={`spin-button ${isSpinning ? 'spinning' : ''} ${remainingSpins <= 0 ? 'disabled' : ''}`}
                            onClick={spinWheel}
                            disabled={isSpinning || remainingSpins <= 0}
                        >
                            {isSpinning ? t('Spinning...') : remainingSpins <= 0 ? t('No spins left') : t('Spin') + '!'}
                        </button>
                    </div>
                </div>

                {/* Items Grid - Original Fortune Design */}
                <section className="ar_item_area_section">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="ar_item_area" style={{backgroundImage: 'url(/images/game-ui/itemBG.png)'}}>
                                    {displayPrizes.map((prize, index) => (
                                        <div 
                                            key={prize.id || index} 
                                            className="ar_singnle_iteam_area"
                                        >
                                            <div className="ar_single_item_img">
                                                <img src="/images/game-ui/items.png" alt="item" />
                                                <div className="ar_single_item_cont">
                                                    <div className="ar_single_item_cont_img">
                                                        <img src={getItemImage(prize)} alt={prize.name} />
                                                    </div>
                                                    <div className="ar_single_item_cont_title">
                                                        <h5>{prize.name}</h5>
                                                    </div>
                                                </div>
                                                {/* Overlay - shows only when spinning or won */}
                                                <div 
                                                    className={`ar_item_overly ${activeItemIndex === index ? 'active' : ''}`}
                                                    style={{backgroundImage: 'url(/images/game-ui/itemHover.png)'}}
                                                >
                                                    <div className="ar_item_overly_cont">
                                                        <h2>{t('Spin')}</h2>
                                                        <p>({t('You have')} <b>{remainingSpins}</b> {t('chances to spin')})</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                
                {/* Today's Prizes */}
                {prizesWonToday.length > 0 && (
                    <div className="today-prizes-container">
                        <h4>🏆 {t("Today's Prizes")}</h4>
                        <div className="today-prizes-list">
                            {prizesWonToday.map((prize, index) => (
                                <div key={index} className="today-prize-item">
                                    <img src={getItemImage(prize)} alt={prize.name || prize.prize_name} />
                                    <span>{prize.name || prize.prize_name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                
                {/* Prize Won Modal */}
                {showPrizeModal && lastPrize && (
                    <div className="prize-modal-overlay" onClick={closePrizeModal}>
                        <div className="prize-modal" onClick={e => e.stopPropagation()}>
                            <div className="prize-modal-header">
                                <span>🎊</span>
                                <span>🎉</span>
                                <span>🎊</span>
                            </div>
                            <h2>{t('Congratulations!')}</h2>
                            <div className="prize-modal-image">
                                <img src={getItemImage(lastPrize)} alt={lastPrize.name} />
                            </div>
                            <h3>{lastPrize.name}</h3>
                            <p className="prize-quantity">
                                {lastPrize.type === 'item' && `x${lastPrize.quantity}`}
                                {lastPrize.type === 'silver' && `${lastPrize.value} Silver`}
                                {lastPrize.type === 'gold' && `${lastPrize.value} Gold`}
                            </p>
                            <button className="prize-claim-btn" onClick={closePrizeModal}>
                                {t('Awesome!')} 🎁
                            </button>
                        </div>
                    </div>
                )}
            </GoldSiverHeader>
        </div>
    );
}

export default DailyGift;
